import { queryClient } from '../main'
import { useAuthStore } from '../auth/useAuthStore'
import { useLiveQuotesStore } from '../stores/useLiveQuotesStore'
import type {
  OptionChainContract,
  OptionChainResponse,
  Quote,
  QuotesResponse,
} from './types'

// Inbound stream message from server/streamer/session.mjs
interface StreamQuoteMessage {
  type: 'quote'
  symbol: string
  bid?: number
  ask?: number
  last?: number
  bidSize?: number
  askSize?: number
  totalVolume?: number
  netChange?: number
  netPercentChange?: number
  mark?: number
  quoteTime?: number
  tradeTime?: number
  underlyingPrice?: number
  openInterest?: number
  securityStatus?: string
}

interface StreamStatusMessage {
  type: 'status'
  status: 'connecting' | 'online' | 'offline'
}

export interface BookMarketMaker {
  id: string
  size: number
}

export interface BookLevel {
  price: number
  size: number
  mmCount: number
  mms: BookMarketMaker[]
}

export interface StreamBookMessage {
  type: 'book'
  symbol: string
  venue: 'NASDAQ' | 'NYSE'
  time?: number
  bids: BookLevel[]
  asks: BookLevel[]
}

type StreamMessage =
  | StreamQuoteMessage
  | StreamStatusMessage
  | StreamBookMessage

const OCC_OPTION_RE = /^[A-Z]+\d{6}[CP]\d{8}$/
const RECONNECT_DELAY_MS = 3_000

export interface LiveQuoteTick {
  symbol: string
  last?: number
  bid?: number
  ask?: number
  mark?: number
  totalVolume?: number
  netChange?: number
  netPercentChange?: number
  tradeTime?: number
  quoteTime?: number
  securityStatus?: string
}

type TickListener = (tick: LiveQuoteTick) => void
const tickListeners = new Map<string, Set<TickListener>>()

export function addTickListener(symbol: string, fn: TickListener): () => void {
  const set = tickListeners.get(symbol) ?? new Set<TickListener>()
  set.add(fn)
  tickListeners.set(symbol, set)
  return () => {
    const cur = tickListeners.get(symbol)
    if (!cur) return
    cur.delete(fn)
    if (cur.size === 0) tickListeners.delete(symbol)
  }
}

function emitTick(msg: StreamQuoteMessage) {
  const set = tickListeners.get(msg.symbol)
  if (!set || set.size === 0) return
  const tick: LiveQuoteTick = {
    symbol: msg.symbol,
    last: msg.last,
    bid: msg.bid,
    ask: msg.ask,
    mark: msg.mark,
    totalVolume: msg.totalVolume,
    netChange: msg.netChange,
    netPercentChange: msg.netPercentChange,
    tradeTime: msg.tradeTime,
    quoteTime: msg.quoteTime,
    securityStatus: msg.securityStatus,
  }
  for (const fn of set) {
    try { fn(tick) } catch { /* listener errors don't break the stream */ }
  }
}

// Subscription refcounts so two components watching the same symbol share one
// upstream subscribe and the symbol only releases when the last consumer
// unsubscribes.
const refs = new Map<string, number>()

// Listener-based channels (level-2 book, time & sales) share the quote
// socket but refcount independently and use their own wire actions.
interface StreamChannel<TMsg extends { symbol: string }> {
  subscribeAction: string
  unsubscribeAction: string
  refs: Map<string, number>
  pending: Set<string> // sent once ws opens
  listeners: Map<string, Set<(msg: TMsg) => void>>
}

function makeChannel<TMsg extends { symbol: string }>(
  subscribeAction: string,
  unsubscribeAction: string,
): StreamChannel<TMsg> {
  return {
    subscribeAction,
    unsubscribeAction,
    refs: new Map(),
    pending: new Set(),
    listeners: new Map(),
  }
}

const bookChannel = makeChannel<StreamBookMessage>('subscribeBook', 'unsubscribeBook')
// Lifecycle code (open/close/visibility) only touches refs + pending.
const channels: Pick<StreamChannel<never>, 'subscribeAction' | 'refs' | 'pending'>[] = [
  bookChannel,
]

let ws: WebSocket | null = null
let pendingSubs = new Set<string>() // sent once ws opens
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let unsubAuthListener: (() => void) | null = null
let visibilityHandler: (() => void) | null = null
let pausedForVisibility = false

function schwabAttachmentId(): string | null {
  const { platforms } = useAuthStore.getState()
  return platforms.find((p) => p.platformId === 'schwab')?.attachmentId ?? null
}

export function isLiveQuotesAvailable(): boolean {
  return schwabAttachmentId() != null
}

function ensureVisibilityListener() {
  if (visibilityHandler || typeof document === 'undefined') return
  // Pause the upstream Schwab Streamer session while the tab is hidden so
  // we don't burn quota on a feed nobody is watching. On refocus, rebuild
  // the socket (re-sending all active refs) and invalidate candle queries
  // so any bars that ticked over while hidden are backfilled.
  visibilityHandler = () => {
    if (document.hidden) {
      if (!hasActiveStreamRefs()) return
      pausedForVisibility = true
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
      if (ws) {
        // Null out before close() so the socket's own 'close' handler
        // (identity check `ws === sock`) skips its reconnect bookkeeping.
        const sock = ws
        ws = null
        try { sock.close() } catch {}
      }
      pendingSubs.clear()
      for (const ch of channels) ch.pending.clear()
      useLiveQuotesStore.setState({ status: 'offline' })
    } else {
      if (!pausedForVisibility) return
      pausedForVisibility = false
      if (!hasActiveStreamRefs()) return
      // Queue every active ref so the next 'open' re-sends the full set.
      for (const s of refs.keys()) pendingSubs.add(s)
      for (const ch of channels) {
        for (const s of ch.refs.keys()) ch.pending.add(s)
      }
      openSocket()
      // Backfill candles that may have closed while the tab was hidden.
      queryClient.invalidateQueries({ queryKey: ['ticker-bars'] })
      queryClient.invalidateQueries({ queryKey: ['prev-day-bars'] })
    }
  }
  document.addEventListener('visibilitychange', visibilityHandler)
}

function ensureAuthListener() {
  if (unsubAuthListener) return
  // If the user attaches or detaches Schwab while connected, drop the
  // existing ws so the next subscribe rebuilds it against the new state.
  unsubAuthListener = useAuthStore.subscribe((state, prev) => {
    const cur = state.platforms.find((p) => p.platformId === 'schwab')?.attachmentId ?? null
    const old = prev.platforms.find((p) => p.platformId === 'schwab')?.attachmentId ?? null
    if (cur !== old) {
      closeSocket()
      if (hasActiveStreamRefs() && cur) openSocket()
    }
  })
}

function openSocket() {
  const attachmentId = schwabAttachmentId()
  if (!attachmentId) {
    useLiveQuotesStore.setState({ status: 'unavailable' })
    return
  }
  // Defer the socket while the tab is hidden. The visibilitychange handler
  // will reopen on refocus and queue all active refs.
  if (typeof document !== 'undefined' && document.hidden) {
    pausedForVisibility = true
    useLiveQuotesStore.setState({ status: 'offline' })
    return
  }
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return

  useLiveQuotesStore.setState({ status: 'connecting' })
  // Build the stream URL from the same base src/api/http.ts uses. When
  // VITE_API_URL is set (Pages cross-origin), this yields wss://api-beta.<domain>;
  // empty (Worker same-origin / Vite dev proxy) falls back to the page origin.
  const apiBase = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')
  const wsBase = apiBase
    ? apiBase.replace(/^http(s?):\/\//i, (_m: string, s: string) => (s ? 'wss://' : 'ws://'))
    : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`
  const url = `${wsBase}/api/stream?attachmentId=${encodeURIComponent(attachmentId)}`
  // Bind the socket locally so handlers don't touch the module `ws`, which
  // `closeSocket` (or a re-connect) can null/swap before 'open' fires. The
  // identity check on close also ignores events from sockets that have been
  // replaced.
  const sock = new WebSocket(url)
  ws = sock
  sock.addEventListener('open', () => {
    if (sock.readyState !== WebSocket.OPEN) return
    if (pendingSubs.size > 0) {
      sock.send(JSON.stringify({ action: 'subscribe', symbols: [...pendingSubs] }))
      pendingSubs.clear()
    }
    for (const ch of channels) {
      if (ch.pending.size === 0) continue
      sock.send(JSON.stringify({ action: ch.subscribeAction, symbols: [...ch.pending] }))
      ch.pending.clear()
    }
  })
  sock.addEventListener('message', (ev) => {
    let msg: StreamMessage
    try { msg = JSON.parse(ev.data) } catch { return }
    if (msg.type === 'status') {
      useLiveQuotesStore.setState({ status: msg.status })
    } else if (msg.type === 'quote') {
      applyQuoteToCache(msg)
      emitTick(msg)
    } else if (msg.type === 'book') {
      emitChannel(bookChannel, msg)
    }
  })
  sock.addEventListener('close', () => {
    if (ws === sock) {
      ws = null
      useLiveQuotesStore.setState({ status: 'offline' })
      if (hasActiveStreamRefs() && schwabAttachmentId()) scheduleReconnect()
    }
  })
  sock.addEventListener('error', () => {
    // 'close' handler runs after 'error' and does the reconnect bookkeeping.
  })
}

function scheduleReconnect() {
  if (reconnectTimer) return
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    openSocket()
  }, RECONNECT_DELAY_MS)
}

function closeSocket() {
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
  if (ws) {
    try { ws.close() } catch {}
    ws = null
  }
  pendingSubs.clear()
  for (const ch of channels) ch.pending.clear()
}

function hasActiveStreamRefs(): boolean {
  return refs.size > 0 || channels.some((ch) => ch.refs.size > 0)
}

function sendSubscribe(symbols: string[]) {
  if (!symbols.length) return
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ action: 'subscribe', symbols }))
  } else {
    for (const s of symbols) pendingSubs.add(s)
  }
}

function sendUnsubscribe(symbols: string[]) {
  if (!symbols.length) return
  for (const s of symbols) pendingSubs.delete(s)
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ action: 'unsubscribe', symbols }))
  }
}

export function subscribe(symbols: string[]) {
  if (!isLiveQuotesAvailable()) {
    useLiveQuotesStore.setState({ status: 'unavailable' })
    return
  }
  ensureAuthListener()
  ensureVisibilityListener()
  const fresh: string[] = []
  for (const s of symbols) {
    if (!s) continue
    const prev = refs.get(s) ?? 0
    refs.set(s, prev + 1)
    if (prev === 0) fresh.push(s)
  }
  if (!ws) openSocket()
  if (fresh.length) sendSubscribe(fresh)
}

export function unsubscribe(symbols: string[]) {
  const drop: string[] = []
  for (const s of symbols) {
    if (!s) continue
    const cur = refs.get(s) ?? 0
    if (cur <= 1) {
      refs.delete(s)
      drop.push(s)
    } else {
      refs.set(s, cur - 1)
    }
  }
  if (drop.length) sendUnsubscribe(drop)
  closeSocketIfIdle()
}

function closeSocketIfIdle() {
  if (!hasActiveStreamRefs()) closeSocket()
}

// ---------- listener channels (book, time & sales) ----------

function emitChannel<TMsg extends { symbol: string }>(ch: StreamChannel<TMsg>, msg: TMsg) {
  const set = ch.listeners.get(msg.symbol)
  if (!set || set.size === 0) return
  for (const fn of set) {
    try { fn(msg) } catch { /* listener errors don't break the stream */ }
  }
}

function subscribeChannel<TMsg extends { symbol: string }>(
  ch: StreamChannel<TMsg>,
  symbol: string,
  fn: (msg: TMsg) => void,
): () => void {
  if (!symbol || !isLiveQuotesAvailable()) return () => {}
  ensureAuthListener()
  ensureVisibilityListener()
  const set = ch.listeners.get(symbol) ?? new Set<(msg: TMsg) => void>()
  set.add(fn)
  ch.listeners.set(symbol, set)
  const prev = ch.refs.get(symbol) ?? 0
  ch.refs.set(symbol, prev + 1)
  if (!ws) openSocket()
  if (prev === 0) {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ action: ch.subscribeAction, symbols: [symbol] }))
    } else {
      ch.pending.add(symbol)
    }
  }
  let released = false
  return () => {
    if (released) return
    released = true
    const cur = ch.listeners.get(symbol)
    cur?.delete(fn)
    if (cur && cur.size === 0) ch.listeners.delete(symbol)
    const count = ch.refs.get(symbol) ?? 0
    if (count <= 1) {
      ch.refs.delete(symbol)
      ch.pending.delete(symbol)
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ action: ch.unsubscribeAction, symbols: [symbol] }))
      }
    } else {
      ch.refs.set(symbol, count - 1)
    }
    closeSocketIfIdle()
  }
}

/**
 * Subscribe to level-2 depth (NASDAQ + NYSE books) for one equity symbol.
 * Snapshots stream to `fn`; returns an unsubscribe function. Shares the
 * quote socket and its visibility/auth lifecycle.
 */
export function subscribeBook(
  symbol: string,
  fn: (msg: StreamBookMessage) => void,
): () => void {
  return subscribeChannel(bookChannel, symbol, fn)
}

// ---------- cache integration ----------

function isoOrPrev(quoteTimeMs: number | undefined, prev: string | undefined): string {
  if (typeof quoteTimeMs === 'number' && Number.isFinite(quoteTimeMs)) {
    return new Date(quoteTimeMs).toISOString()
  }
  return prev ?? ''
}

function mergeQuote(prev: Quote | undefined, msg: StreamQuoteMessage): Quote {
  const isOption = OCC_OPTION_RE.test(msg.symbol)
  const oneDayChange = msg.netChange != null
    ? {
        change: String(msg.netChange),
        percentChange: String(msg.netPercentChange ?? prev?.oneDayChange?.percentChange ?? 0),
      }
    : prev?.oneDayChange ?? null
  return {
    instrument: prev?.instrument ?? {
      symbol: msg.symbol,
      type: isOption ? 'OPTION' : 'EQUITY',
    },
    outcome: 'SUCCESS',
    last: msg.last != null ? String(msg.last) : prev?.last ?? '0',
    lastTimestamp: isoOrPrev(msg.tradeTime ?? msg.quoteTime, prev?.lastTimestamp),
    bid: msg.bid != null ? String(msg.bid) : prev?.bid ?? '0',
    bidSize: msg.bidSize ?? prev?.bidSize ?? 0,
    ask: msg.ask != null ? String(msg.ask) : prev?.ask ?? '0',
    askSize: msg.askSize ?? prev?.askSize ?? 0,
    volume: msg.totalVolume ?? prev?.volume ?? 0,
    previousClose: prev?.previousClose ?? '0',
    oneDayChange,
    securityStatus: msg.securityStatus ?? prev?.securityStatus ?? null,
  }
}

function mergeOptionContract(
  prev: OptionChainContract,
  msg: StreamQuoteMessage,
): OptionChainContract {
  const bidNum = msg.bid != null ? msg.bid : Number(prev.bid)
  const askNum = msg.ask != null ? msg.ask : Number(prev.ask)
  const computedMid =
    Number.isFinite(bidNum) && Number.isFinite(askNum) && bidNum > 0 && askNum > 0
      ? (bidNum + askNum) / 2
      : null
  const midSource = msg.mark != null ? msg.mark : computedMid
  return {
    ...prev,
    last: msg.last != null ? String(msg.last) : prev.last,
    lastTimestamp: isoOrPrev(msg.tradeTime ?? msg.quoteTime, prev.lastTimestamp),
    bid: msg.bid != null ? String(msg.bid) : prev.bid,
    bidSize: msg.bidSize ?? prev.bidSize,
    ask: msg.ask != null ? String(msg.ask) : prev.ask,
    askSize: msg.askSize ?? prev.askSize,
    volume: msg.totalVolume ?? prev.volume,
    openInterest: msg.openInterest ?? prev.openInterest,
    optionDetails: {
      ...prev.optionDetails,
      midPrice:
        midSource != null && Number.isFinite(midSource)
          ? String(midSource)
          : prev.optionDetails.midPrice,
    },
  }
}

function applyTickToOptionChainCache(msg: StreamQuoteMessage) {
  if (!OCC_OPTION_RE.test(msg.symbol)) return
  queryClient.setQueriesData<OptionChainResponse>(
    { queryKey: ['option-chain'] },
    (old) => {
      if (!old) return old
      const patchList = (list: OptionChainContract[] | undefined) => {
        if (!list || list.length === 0) return { list, changed: false }
        const idx = list.findIndex((c) => c?.instrument?.symbol === msg.symbol)
        if (idx < 0 || !list[idx]) return { list, changed: false }
        const next = list.slice()
        next[idx] = mergeOptionContract(list[idx], msg)
        return { list: next, changed: true }
      }
      const calls = patchList(old.calls)
      const puts = patchList(old.puts)
      if (!calls.changed && !puts.changed) return old
      return { ...old, calls: calls.list!, puts: puts.list! };
    },
  )
}

function applyQuoteToCache(msg: StreamQuoteMessage) {
  // Update every ['quote', symbol, ...] cache (accountId is the trailing
  // key var; we partial-match on the prefix).
  queryClient.setQueriesData<QuotesResponse>(
    { queryKey: ['quote', msg.symbol] },
    (old) => {
      const prevQuote = old?.quotes?.[0]
      return { quotes: [mergeQuote(prevQuote, msg)] }
    },
  )
  // Update every ['active-quotes', symbolKey] cache (symbolKey is the
  // comma-joined symbol list — partial-match the prefix).
  queryClient.setQueriesData<QuotesResponse>(
    { queryKey: ['active-quotes'] },
    (old) => {
      if (!old) return old
      const list = old.quotes ?? []
      const idx = list.findIndex((q) => q?.instrument?.symbol === msg.symbol)
      if (idx < 0 || !list[idx]) return old
      const next = list.slice()
      next[idx] = mergeQuote(list[idx], msg)
      return { quotes: next }
    },
  )
  applyTickToOptionChainCache(msg)
}
